<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  forms: { type: Array, default: () => [] },
  selectedFormId: { type: [Number, String], default: null },
});

defineEmits(['select', 'add']);

const { t } = useI18n();

const searchQuery = ref('');

const filteredForms = computed(() => {
  if (!searchQuery.value) return props.forms;
  const q = searchQuery.value.toLowerCase();
  return props.forms.filter(
    form =>
      (form.name || '').toLowerCase().includes(q) ||
      (form.category_name || '').toLowerCase().includes(q) ||
      (form.subcategory_name || '').toLowerCase().includes(q)
  );
});

// "Category › Subcategory" — drops either part when absent (e.g. contact forms).
function categoryLine(form) {
  return [form.category_name, form.subcategory_name].filter(Boolean).join(' › ');
}
</script>

<template>
  <div class="w-64 border-r border-n-weak flex flex-col bg-n-slate-1 shrink-0">
    <div class="p-3 border-b border-n-weak">
      <div class="flex items-center justify-between mb-2">
        <span
          class="text-xs font-semibold text-n-slate-9 uppercase tracking-wider"
        >
          {{ t('FORM_BUILDER.LIST.TITLE') }}
        </span>
        <button
          class="w-6 h-6 rounded-md bg-n-alpha-2 hover:bg-n-alpha-3 flex items-center justify-center text-n-slate-11 hover:text-n-slate-12 transition-colors"
          :title="t('FORM_BUILDER.LIST.ADD')"
          @click="$emit('add')"
        >
          <svg
            class="w-3.5 h-3.5"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            viewBox="0 0 24 24"
          >
            <path d="M12 4v16m8-8H4" />
          </svg>
        </button>
      </div>
      <input
        v-model="searchQuery"
        type="text"
        :placeholder="t('FORM_BUILDER.LIST.SEARCH')"
        class="w-full px-2.5 py-1.5 rounded-md bg-n-alpha-1 border border-n-weak text-xs text-n-slate-12 placeholder-n-slate-9"
      />
    </div>

    <div class="flex-1 overflow-y-auto p-2 space-y-1">
      <div
        v-for="form in filteredForms"
        :key="form.id"
        class="rounded-lg p-2.5 border cursor-pointer transition-all"
        :class="
          selectedFormId === form.id
            ? 'border-woot-500/30 bg-n-blue-2/50'
            : 'border-n-weak hover:border-n-strong hover:bg-n-alpha-1'
        "
        @click="$emit('select', form.id)"
      >
        <div class="flex items-center gap-2 mb-1">
          <span
            class="w-2 h-2 rounded-full shrink-0"
            :class="form.active ? 'bg-emerald-500' : 'bg-n-slate-9'"
          />
          <span class="text-sm font-medium text-n-slate-12 truncate">
            {{ form.name }}
          </span>
        </div>
        <p class="text-[11px] text-n-slate-10 ml-4 truncate">
          {{ form.active ? '' : t('FORM_BUILDER.LIST.INACTIVE') + ' · '
          }}{{ categoryLine(form) }}
        </p>
      </div>

      <div
        v-if="filteredForms.length === 0"
        class="py-8 text-center text-sm text-n-slate-9"
      >
        {{ t('FORM_BUILDER.LIST.EMPTY') }}
      </div>
    </div>
  </div>
</template>
